import {
	PendingView,
	useAsync,
	useBarcodeListener,
	useKeypadListener,
	usePlatform,
	useStorageItemValue,
	View,
	ViewContent
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {stylesheet} from "typestyle";
import {
	BackendApiCredentials,
	Carriers,
	CubeCarrierState,
	getDeliveryOccupancyContent,
	OutboundDelivery
} from "./types";
import {AccessCode, AccessCodeAlphabet, Barcode, Occupancy} from "@variocube/app-api";
import {parseBarcode} from "./barcode";
import {CarrierViewHeader} from "./CarrierViewHeader";
import {useAuthenticatedCarrier, useCarrierId, useSetAuthenticatedCarrier, useSetCarrierId} from "./CarrierProvider";
import {defined} from "@variocube/app-common";
import {logger} from "./logger";
import {InvalidCode, InvalidCodeType} from "./InvalidCodeDialog";
import {CarrierCodeFormView} from './CarrierCodeFormView';
import {CarrierStoreEventHandler} from "./carrier-store/carrier-store-event-handlers";
import {useSetMessage} from "./DialogMessageProvider";
import {KepServiceError} from "./rest-clients/DhlRestClient";
import {CodeForm} from "./input/CodeForm";
import {physicalAdapter} from "./physical";
import {useLocalization} from "../i18n";

interface HomeViewProps {
    carriers?: Carriers;
    tenantLogoUrl?: string;
    onCarrierStore: (occupancy: Occupancy) => void;
    onCustomerPickup: (occupancies: Occupancy[]) => void;
    onCarrierRetrieval: (occupancies: Occupancy[]) => void;
    onCustomerStore: (delivery: OutboundDelivery) => void;
    backendApiCredentials?: BackendApiCredentials;
}

interface CarrierSecondFactor {
    pin: string;
    carrierId: string;
}

const log = logger.sub("HomeView");

export function HomeView(props: HomeViewProps) {
    const {
        carriers,
        onCarrierStore,
        onCustomerPickup,
        onCarrierRetrieval,
        onCustomerStore,
        backendApiCredentials
    } = props;

    const {t} = useLocalization();
    const platform = usePlatform();
    const carrier = useCarrierId();
    const setCarrier = useSetCarrierId();
    const setAuthenticatedCarrier = useSetAuthenticatedCarrier();
    const authenticatedCarrier = useAuthenticatedCarrier();
    const [loadingEventHandler, setLoadingEventHandler] = useState<boolean>();
    const carrierStoreEventHandler = useMemo(() => new CarrierStoreEventHandler(), []);
    const [dhlShipmentQueryParcelId, setDhlShipmentQueryParcelId] = useState<string | undefined>();

    const setMessage = useSetMessage();
    const expiredParcelIds = useStorageItemValue<string[] | undefined>("expiredDeliveryIds");

    const [carrierSecondFactor, setCarrierSecondFactor] = useState<CarrierSecondFactor>();
    const [invalidCode, setInvalidCode] = useState<InvalidCode | undefined>();

    const findOccupanciesByDeliveryId = useCallback(async (deliveryId: string) => {
        return (await platform.getOccupancies()).filter(o => o.content.deliveryId === deliveryId);
    }, [platform]);

    const lastMileEnabled = useMemo<boolean>(() => {
        if(carriers && authenticatedCarrier) {
            const carrier = carriers[authenticatedCarrier];
            if(carrier && carrier.state) {
                switch (carrier.state) {
                    case CubeCarrierState.Disabled:
                    case CubeCarrierState.FirstMileOnly:
                        return false;
                }
            }
        }
        return true;
    }, [carriers, authenticatedCarrier]);

    useAsync(async () => {
        if (dhlShipmentQueryParcelId && carrier) {
            await handleDhlShipmentQuery(dhlShipmentQueryParcelId, carrier);
            setDhlShipmentQueryParcelId(undefined);
        }
    }, [dhlShipmentQueryParcelId, carrier]);

    const handleCodeVerify = useCallback(async (code: string, passedCarrierId?: string) => {
        setInvalidCode(undefined);
        log.info("Checking code", code);
        const currentCarrierId = passedCarrierId ?? carrier;
        console.log({currentCarrierId});
        if (code) {
            if (carriers) {
                const foundCarrier = Object.entries(carriers)
                    .filter(([, value]) => [value.accessCode, value.fixedAccessCode].filter(defined).includes(code))
                    .pop();
                if (foundCarrier && foundCarrier[0] === currentCarrierId) {
                    const [carrierId, {pin}] = foundCarrier;

                    log.info("Code is carrier access code for carrier", carrierId);

                    if (pin) {
                        setCarrierSecondFactor({
                            pin,
                            carrierId,
                        });
                        return;
                    }
                    else {
                        return setAuthenticatedCarrier(carrierId);
                    }
                }
            }

            if(physicalAdapter.isPhysicalContainerUrl(code)) {
                code = await physicalAdapter.fetchStorageCode(code);
            }

            const occupancies = (await platform.getOccupancies())
                .filter((occupancy) => {
                    const delivery = getDeliveryOccupancyContent(occupancy);
                    if (delivery && delivery.carrierId == currentCarrierId) {
                        // If the occupancy access code was entered by the user it is a pickup by a customer
                        if (occupancy.accessCode == code) {
                            return true;
                        }
                        // For the other cases we need to check the occupancy content
                        if (delivery.deliveryId == code || delivery.carrierDeliveryCode == code || delivery.carrierRetrievalCode == code || delivery.recipientPickupCode == code) {
                            return true;
                        }
                    }
                    return false;
                });

            const occupancy = occupancies.pop();
            if (occupancy) {
                log.info("Found occupancy", occupancy);
                const delivery = getDeliveryOccupancyContent(occupancy);
                if (delivery && delivery.carrierId === currentCarrierId) {
                    if (delivery.barCode) {
                        return onCarrierRetrieval([occupancy]);
                    }
                    switch (code) {
                        case delivery.deliveryId:
                        case delivery.carrierDeliveryCode:
                            return onCarrierStore(occupancy);

                        case delivery.carrierRetrievalCode:
                            return onCarrierRetrieval([occupancy]);

                        case delivery.recipientPickupCode:
                            const occupanciesFromCarrier = occupancies.filter(o => {
                               const deliveryContent = getDeliveryOccupancyContent(o);
                               return deliveryContent?.carrierId === currentCarrierId && code === deliveryContent?.recipientPickupCode;
                            });
                            console.log({occupanciesFromCarrier});
                            return onCustomerPickup([...occupanciesFromCarrier, occupancy]);
                    }
                }
            }
        }
        throw new Error("Invalid code");
    }, [platform, onCarrierStore, onCustomerPickup, onCarrierRetrieval, carriers, carrier]);

    const handleDhlShipmentQuery = useCallback(async (code: string, carrierId: string) => {
        setLoadingEventHandler(true);
        try {
            const res = await carrierStoreEventHandler.parcelScanned({
                carrierId: carrierId,
                parcelId: code,
                backendApiCredentials: backendApiCredentials!,
                cubeId: platform.hostname,
            });
            if (res?.size && res?.shipmentId && !res?.errorCode) {
                const occupancy = await platform.occupy({
                    type: res.size,
                    accessCodeShape: {
                        alphabet: AccessCodeAlphabet.OneToNine,
                        length: 6,
                    },
                    content: {
                        deliveryId: code,
                        carrierId: carrierId,
                        isAdhocDelivery: true,
                        shipmentId: res?.shipmentId
                    },
                })

                await platform.confirmOccupancy(occupancy.uuid);
                return onCarrierStore(occupancy);
            }
        } catch (err) {
            if (err instanceof KepServiceError) {
                const res = err.body;
                switch (res?.errorCode) {
                    case 'NO_FITTING_BOX_AVAILABLE':
                        setMessage({
                            title: t("NoFittingBoxAvailableTitle"),
                            message: t("NoFittingBoxAvailableDescription"),
                        });
                        break;
                    case 'INVALID_PACKAGE':
                    default:
                        console.error("Storing parcel from dhl is not allowed");
                        setMessage({
                            title: t("ParcelNotAcceptedTitle"),
                            message: t("ParcelNotAcceptedDescription", { barCode: code }),
                        });
                        break;
                }
            } else {
                console.error("There was an error when trying to store dhl parcel", err);
                setMessage({
                    title: t("ParcelNotAcceptedTitle"),
                    message: t("ParcelNotAcceptedDescription", {barCode: code}),
                });
            }
        } finally {
            setLoadingEventHandler(false);
        }
    }, [setLoadingEventHandler, carrierStoreEventHandler, backendApiCredentials, platform, onCarrierStore, setMessage]);

    const handleBarcode = useCallback(async (barcode: Barcode) => {
        if (authenticatedCarrier || loadingEventHandler) return;
        const barCode = atob(barcode.code);

        const result = parseBarcode(barCode);
        if (result) {
            const {carrier, parcelNo} = result;
            setCarrier(carrier);
            console.log(`Checking parcelNo: ${parcelNo} of carrier: ${carrier} against list of expired parcel numbers`);
            if (expiredParcelIds && expiredParcelIds.indexOf(parcelNo) != -1) {
                setInvalidCode({
                    type: InvalidCodeType.Expired,
                    code: parcelNo
                });
                return;
            }
            try {
                log.info("Found parcelNo in barcode. Trying parcelNo for access code", result);
                return await handleCodeVerify(parcelNo, carrier);
            } catch (error) {
                if(carrier === "dhl" && !(await findOccupanciesByDeliveryId(parcelNo))?.length && lastMileEnabled) {
                    return setDhlShipmentQueryParcelId(parcelNo);
                }

                if (result.type == "outbound") {
                    log.info("Found outbound parcel.", result);
                    return onCustomerStore({barcode: barCode, needSender: false, carrier});
                }
                else if (result.type == "return") {
                    log.info("Found return parcel.", result);
                    return onCustomerStore({barcode: barCode, needSender: true, carrier});
                }
            }
        }

        // Barcode can be access codes
        try {
            await handleCodeVerify(barCode);
        } catch (e) {
            setInvalidCode({
                type: InvalidCodeType.Invalid,
                code: barCode
            });
        }
    }, [onCustomerStore, handleCodeVerify, setCarrier, expiredParcelIds, loadingEventHandler]);
    useBarcodeListener(handleBarcode);

    const handleCarrierPinBack = useCallback(() => setCarrierSecondFactor(undefined), []);
    const handleCarrierPinSuccess = useCallback(() => {
        if (carrierSecondFactor) {
            setAuthenticatedCarrier(carrierSecondFactor.carrierId);
        }
    }, [setAuthenticatedCarrier, carrierSecondFactor]);

	const handleKeypad = useCallback(async (accessCode: AccessCode) => {
		console.info("Keypad code entered", accessCode);
		try {
			await handleCodeVerify(accessCode.code);
		} catch (err) {
			console.error('Failed to process entered key', err);
			setInvalidCode({ code: accessCode.code, type: InvalidCodeType.InvalidKey });
		}
	}, [handleCodeVerify, setInvalidCode]);
	useKeypadListener(handleKeypad);

    if (loadingEventHandler) {
        return <PendingView message={t("ProcessingParcelStore")} />;
    }

    if (carrierSecondFactor) {
        return (
            <CarrierPinView
                pin={carrierSecondFactor.pin}
                onBack={handleCarrierPinBack}
                onSuccess={handleCarrierPinSuccess}
            />
        );
    }

    return (
        <CarrierCodeFormView
            carrier={carrier}
            handleCodeVerify={handleCodeVerify}
            setInvalidCode={setInvalidCode}
            invalidCode={invalidCode}
        />
    );
}



interface CarrierPinView {
    pin: string;
    onBack: () => void;
    onSuccess: () => void;
}

function CarrierPinView({pin, onBack, onSuccess}: CarrierPinView) {
    const {t} = useLocalization();

    const handleVerify = useCallback(async (code: string) => {
        if (code == pin) {
            onSuccess();
        }
        throw new Error("Invalid code");
    }, [pin, onSuccess]);

    return (
        <View>
            <CarrierViewHeader onBack={onBack} title={t("EnterCarrierPin")}
                               description={t("EnterCarrierPinDescription")}/>
            <ViewContent className={styles.content}>
                <CodeForm onVerify={handleVerify} strings={t} className={styles.codeForm} revealInput/>
            </ViewContent>
        </View>
    );
}

const styles = stylesheet({
    carrierLogo: {
        width: "auto",
        height: "4rem",
        margin: "0.5em 1.5em",
        alignSelf: "center"
    },
    tenantLogo: {
        width: "auto",
        height: "5rem",
        margin: "0"
    },
    viewTitle: {
        flexGrow: 10
    },
    text: {
        fontSize: "1.75rem",
        textAlign: "center",
        padding: "1rem 2rem"
    },
    content: {
        display: "flex",
        alignItems: "center",
        justifyContent: "stretch",
    },
    codeForm: {
        flex: 1,
    },
    helpButton: {
        width: "4rem",
        paddingTop: "0.5rem",
        flexGrow: 4
    }
});

